import React, { useState, useEffect, useCallback, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import GLOBALVAR from "../constants";
import { Spinner, Alert, Toast, ToastContainer, Badge } from "react-bootstrap";
import debounce from 'lodash/debounce';
import '../css/autosuggest.scss';

function AutosuggestComponent({ setApplicantDetails, setLoanProcessingDetails }) {
    const [value, setValue] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchInfo, setSearchInfo] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const debouncedSearchRef = useRef(null);
    const toastTimeoutRef = useRef(null);

    const fetchApplicantAndLoans = useCallback(async (applicantId) => {
        try {
            setLoading(true);
            setError(null);

            // Fetch applicant details
            const applicantResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applications/${applicantId}`);
            const applicantData = applicantResponse.data;

            // Fetch loan processing details for the applicant
            const loansResponse = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/loan-processing/fetch/by-applicant/${applicantId}`);
            const loanData = loansResponse.data;

            setApplicantDetails(applicantData);
            setLoanProcessingDetails(loanData);
        } catch (error) {
            console.error('Error fetching applicant and loans:', error);
            setError(error.response?.data?.message || 'Error fetching applicant details');
        } finally {
            setLoading(false);
        }
    }, [setApplicantDetails, setLoanProcessingDetails]);

    const handleSelectChange = useCallback((selectedOption) => {
        if (selectedOption) {
            setApplicantDetails(null);
            setLoanProcessingDetails([]);
            fetchApplicantAndLoans(selectedOption.value);
        }
    }, [fetchApplicantAndLoans, setApplicantDetails, setLoanProcessingDetails]);

    const searchApplications = useCallback(async (searchValue) => {
        if (searchValue.length < 2) {
            setOptions([]);
            setSearchInfo(null);
            setShowToast(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const response = await axios.get(`${GLOBALVAR['SERVER_ROOT']}/api/applicationsearch`, {
                params: {
                    query: searchValue,
                    page: 1,
                    limit: 10
                }
            });

            const data = response.data;
            if (data && data.rows) {
                const suggestions = data.rows.map(application => ({
                    label: `${application.lastName}, ${application.firstName} ${application.middleName}`,
                    value: application.id,
                    data: {
                        email: application.emailAddress,
                        mobile: application.mobileNo1,
                        address: `${application.street}, ${application.barangay}, ${application.municipality}`
                    }
                }));
                setOptions(suggestions);
                setSearchInfo(data.searchCriteria);
            } else {
                setOptions([]);
            }
        } catch (error) {
            console.error('Error fetching suggestions:', error);
            setError(error.response?.data?.message || 'Error searching applications');
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Create debounced search function
        debouncedSearchRef.current = debounce((searchValue) => {
            searchApplications(searchValue);
        }, 300);

        return () => {
            if (debouncedSearchRef.current) {
                debouncedSearchRef.current.cancel();
            }
        };
    }, [searchApplications]);

    useEffect(() => {
        if (debouncedSearchRef.current) {
            debouncedSearchRef.current(value);
        }
    }, [value]);

    // Better toast handling with cleanup
    useEffect(() => {
        if (searchInfo) {
            setShowToast(true);
            
            // Clear any existing timeout
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
            
            // Set a timeout to hide toast if user keeps typing
            toastTimeoutRef.current = setTimeout(() => {
                setShowToast(false);
            }, 5000); // Extended duration for better visibility
        }
        
        return () => {
            if (toastTimeoutRef.current) {
                clearTimeout(toastTimeoutRef.current);
            }
        };
    }, [searchInfo]);

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: state.isFocused ? '#80bdff' : '#ced4da',
            boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,.25)' : 'none',
            '&:hover': {
                borderColor: '#80bdff'
            }
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#f8f9fa' : 'white',
            color: state.isSelected ? 'white' : '#212529',
            '&:hover': {
                backgroundColor: state.isSelected ? '#0056b3' : '#e9ecef'
            }
        }),
        menu: (provided) => ({
            ...provided,
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            zIndex: 9999
        }),
        menuList: (provided) => ({
            ...provided,
            padding: '0.5rem 0'
        })
    };

    const formatOptionLabel = useCallback(({ label, data }) => (
        <div>
            <div className="fw-bold">{label}</div>
            {data && (
                <div className="small text-muted">
                    {data.email && <div>{data.email}</div>}
                    {data.mobile && <div>{data.mobile}</div>}
                    {data.address && <div>{data.address}</div>}
                </div>
            )}
        </div>
    ), []);

    return (
        <div className="autosuggest-container mb-4 position-relative">
            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible>
                    {error}
                </Alert>
            )}
            <Select
                options={options}
                isLoading={loading}
                onInputChange={setValue}
                value={options.find(option => option.label === value)}
                placeholder="Search by name, email, or mobile number..."
                onChange={handleSelectChange}
                styles={customStyles}
                formatOptionLabel={formatOptionLabel}
                noOptionsMessage={() => "No applications found"}
                loadingMessage={() => "Searching..."}
                isClearable
                isSearchable
                components={{
                    DropdownIndicator: () => (
                        <div className="px-3">
                            {loading ? (
                                <Spinner animation="border" size="sm" />
                            ) : (
                                <i className="fas fa-search text-muted" />
                            )}
                        </div>
                    )
                }}
            />
            <ToastContainer 
                position="bottom-start" 
                className="p-3 mt-2 autosuggest-toast-container"
                style={{ zIndex: 1000 }}
            >
                <Toast 
                    show={showToast} 
                    onClose={() => setShowToast(false)}
                    delay={5000} 
                    autohide
                >
                    <Toast.Body className="d-flex align-items-center">
                        <i className="fas fa-filter text-primary me-2"></i>
                        <div>
                            <div className="small fw-semibold mb-1">Search Fields:</div>
                            <div>
                                {searchInfo && searchInfo.searchFields.map((field, index) => (
                                    <Badge 
                                        bg="light" 
                                        text="dark" 
                                        className="me-1 mb-1" 
                                        key={index}
                                        style={{ border: '1px solid #dee2e6' }}
                                    >
                                        {field}
                                    </Badge>
                                ))}
                            </div>
                            {searchInfo && searchInfo.resultCount !== undefined && (
                                <div className="small mt-1">
                                    <span className="text-secondary">Found: </span>
                                    <span className="fw-semibold">
                                        {searchInfo.resultCount} {searchInfo.resultCount === 1 ? 'result' : 'results'}
                                    </span>
                                </div>
                            )}
                        </div>
                        <button 
                            type="button" 
                            className="btn-close ms-auto" 
                            aria-label="Close" 
                            onClick={() => setShowToast(false)}
                            style={{ fontSize: '0.75rem' }}
                        />
                    </Toast.Body>
                </Toast>
            </ToastContainer>
        </div>
    );
}

export default AutosuggestComponent;
